import { useCallback, useEffect, useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { StatusBadge } from '@/components/ui/status-badge'
import { Surface } from '@/components/ui/surface'
import { formatDatum } from '@/lib/format'
import { runWithFeedback } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { getListMailchimpStatus } from '../api'
import type { CrmListMailchimpStatus } from '../types'
import { updateListV2 } from './api'
import type { ListDetail } from './types'

/**
 * Mailchimp on a list (spec-49, spec-63 E5): the sync toggle, the tag name, and what the last push did. The
 * status comes from the existing endpoint; an archived list does not sync and says so. A change is saved at once
 * (the toggle) or when the tag field loses focus, and the page reads the list again.
 */
export function MailchimpCard({ list, onChanged }: { list: ListDetail; onChanged: () => void }) {
  const [status, setStatus] = useState<CrmListMailchimpStatus | null>(null)
  const [tagName, setTagName] = useState(list.mailchimp_tag_name ?? '')
  const [saving, setSaving] = useState(false)
  const archived = list.archived_at !== null

  useEffect(() => {
    let stale = false
    getListMailchimpStatus(list.id).then(
      (found) => {
        if (!stale) setStatus(found)
      },
      () => {
        if (!stale) setStatus(null)
      },
    )
    return () => {
      stale = true
    }
  }, [list.id, list.mailchimp_sync_enabled, list.archived_at])

  const save = useCallback(
    async (patch: { mailchimp_sync_enabled?: boolean; mailchimp_tag_name?: string | null }, success: string) => {
      setSaving(true)
      const saved = await runWithFeedback(() => updateListV2(list.id, patch), { error: 'Opslaan mislukt.' })
      setSaving(false)
      if (!saved) return false
      toast.success(success)
      onChanged()
      return true
    },
    [list.id, onChanged],
  )

  function saveTag() {
    const trimmed = tagName.trim()
    if (trimmed === (list.mailchimp_tag_name ?? '')) return
    void save({ mailchimp_tag_name: trimmed || null }, 'Tagnaam opgeslagen.')
  }

  const unconfigured = status?.configured === false
  return (
    <Surface variant="page" className="flex flex-col gap-3">
      <h2 className="font-heading text-lg font-semibold">Mailchimp</h2>
      <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
        <Checkbox
          aria-label="Sync naar Mailchimp"
          checked={list.mailchimp_sync_enabled}
          disabled={saving || unconfigured}
          onChange={(event) => void save({ mailchimp_sync_enabled: event.target.checked }, event.target.checked ? 'Mailchimp-sync aangezet.' : 'Mailchimp-sync uitgezet.')}
        />
        Sync naar Mailchimp
      </label>
      {unconfigured ? <p className="text-sm text-muted-foreground">Mailchimp is nog niet geconfigureerd.</p> : null}
      {archived && list.mailchimp_sync_enabled ? (
        <p className="text-sm text-muted-foreground">{status?.note ?? 'Deze lijst is gearchiveerd: de sync loopt niet zolang ze gearchiveerd is.'}</p>
      ) : null}
      {list.mailchimp_sync_enabled && !unconfigured ? (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1.5 text-sm font-semibold">
            Mailchimp-tagnaam
            <Input value={tagName} onChange={(event) => setTagName(event.target.value)} onBlur={saveTag} placeholder={list.name} className="max-w-xs" />
          </label>
          {status ? (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>
                {status.last_pushed_at ? `Laatst gesynct ${formatDatum(status.last_pushed_at)} · ` : ''}
                {`${status.pushed_count} gepusht · ${status.skipped_no_email} zonder e-mail · ${status.skipped_opt_out} opt-out`}
              </span>
              {status.error_count > 0 ? <StatusBadge tone="danger">{`${status.error_count} fouten`}</StatusBadge> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Surface>
  )
}
